import React, { useEffect, useRef, useState } from 'react';
import Editor, { loader } from '@monaco-editor/react';
import './MonacoEditor.css';
import themesData from '../../../themes/monaco-themes.json';
import { getDefaultTheme, isValidTheme } from '../../../themes/themes';
import { logger } from '../../../core/logger.js';

let themesRegistered = false;

// Register themes synchronously when module loads
const registerThemes = () => {
  if (typeof window === 'undefined' || themesRegistered) return;

  loader.init().then((monaco) => {
    if (themesRegistered) return;

    // Register all themes from the JSON file
    Object.entries(themesData).forEach(([themeName, themeConfig]) => {
      monaco.editor.defineTheme(themeName, themeConfig);
    });

    themesRegistered = true;
  }).catch(error => {
    console.error('[MonacoEditor] Failed to initialize Monaco loader:', error);
  });
};

// Register themes immediately
registerThemes();

export function MonacoEditor({ value, onChange, language = 'plaintext', readOnly = false, onEditorMount, theme = getDefaultTheme() }) {
  // Ensure theme is valid, fallback to default
  const validTheme = isValidTheme(theme) ? theme : getDefaultTheme();
  logger.log(
    { tags: "monaco|editor|render", color1: "cyan" },
    "Rendering Monaco editor with theme",
    { theme: validTheme }
  );

  const editorRef = useRef(null);
  const monacoRef = useRef(null);
  const [themesReady, setThemesReady] = useState(themesRegistered);

  const handleEditorDidMount = (editor, monaco) => {
    editorRef.current = editor;
    monacoRef.current = monaco;


    onEditorMount?.(editor, monaco);
  };

  const handleChange = (val) => {
    onChange(val ?? '');
  };

  // Wait for themes to be registered
  useEffect(() => {
    if (!themesReady && !themesRegistered) {
      const checkThemes = () => {
        if (themesRegistered) {
          setThemesReady(true);
        } else {
          setTimeout(checkThemes, 50);
        }
      };
      checkThemes();
    }
  }, [themesReady]);

  useEffect(() => {
    return () => {
      if (editorRef.current) {
        editorRef.current.dispose();
      }
      monacoRef.current = null;
    };
  }, []);

  if (!themesReady) {
    return (
      <div className="monaco-editor-container">
        <div className="monaco-editor-loading">Loading themes…</div>
      </div>
    );
  }
  return (
    <div className="monaco-editor-container">
      <Editor
        value={value}
        onChange={handleChange}
        language={language || 'plaintext'}
        loading={<div className="monaco-editor-loading">Loading editor…</div>}
        onMount={handleEditorDidMount}
        theme={validTheme}
        options={{
          readOnly,
          minimap: { enabled: false },
          glyphMargin: true,
          automaticLayout: true,
          fontSize: 14,
          scrollBeyondLastLine: false,
          smoothScrolling: true,
          wordWrap: 'on',
          fixedOverflowWidgets: true,
          bracketPairColorization: { enabled: true },
          matchBrackets: 'always',
        }}
      />
    </div>
  );
}